import React from 'react';
import { Link } from 'react-router-dom';
import { DocumentTextIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import DisclaimerFooter from '../DisclaimerFooter';

const QuickReferencePage = () => {
  return (
    <div className="min-h-screen bg-bg-100">
      <div className="px-6 py-8">
        {/* Breadcrumbs */}
        <nav className="mb-6">
          <div className="flex items-center text-sm text-text-300">
            <Link to="/learn" className="hover:text-primary-100 transition-colors">
              Learn
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" />
            <span className="text-text-100">Quick Reference</span>
          </div>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-100 mb-4 flex items-center gap-3">
            <DocumentTextIcon className="h-8 w-8 text-primary-100" />
            Quick Reference
          </h1>
          <p className="text-lg text-text-200">
            Essential facts, schedules, and key information for SharpeMetrix users.
          </p>
        </div>

        {/* Quick Reference Content */}
        <div className="bg-primary-100/5 rounded-lg p-6 border border-primary-100/20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-medium text-text-100 mb-4 text-lg">Monthly Schedule</h3>
              <ul className="text-sm text-text-200 space-y-2">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary-100 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  New portfolios released first trading day of each month
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary-100 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Trade lists provided with specific buy/sell instructions
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary-100 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Performance reports available within 2 business days
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary-100 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Signal updates reflect most recent market data
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-medium text-text-100 mb-4 text-lg">Key Metrics</h3>
              <ul className="text-sm text-text-200 space-y-2">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-accent-100 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Portfolio sizes: 30-50 stocks typically
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-accent-100 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Maximum position size: 2-10% depending on strategy
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-accent-100 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Monthly turnover: 15-25% on average
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-accent-100 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Universes: S&P 500, Russell 1000, Russell 2000
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-medium text-text-100 mb-4 text-lg">Signal Descriptions</h3>
              <div className="space-y-3">
                <div className="bg-bg-200 p-3 rounded-lg">
                  <h4 className="font-medium text-text-100 text-sm mb-1">Value Score</h4>
                  <p className="text-xs text-text-200">Enhanced valuation metrics using forward-looking earnings and sector adjustments</p>
                </div>
                <div className="bg-bg-200 p-3 rounded-lg">
                  <h4 className="font-medium text-text-100 text-sm mb-1">Momentum Score</h4>
                  <p className="text-xs text-text-200">Peer-relative performance analysis controlling for market-wide effects</p>
                </div>
                <div className="bg-bg-200 p-3 rounded-lg">
                  <h4 className="font-medium text-text-100 text-sm mb-1">Quality Score</h4>
                  <p className="text-xs text-text-200">Sustainable earnings, cash flow consistency, and balance sheet strength</p>
                </div>
                <div className="bg-bg-200 p-3 rounded-lg">
                  <h4 className="font-medium text-text-100 text-sm mb-1">Low Vol Score</h4>
                  <p className="text-xs text-text-200">Predictive volatility component that historically leads to positive returns</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-medium text-text-100 mb-4 text-lg">Alternative Data Signals</h3>
              <div className="space-y-3">
                <div className="bg-bg-200 p-3 rounded-lg">
                  <h4 className="font-medium text-text-100 text-sm mb-1">Earnings Score</h4>
                  <p className="text-xs text-text-200">Analyst estimate quality, peer momentum, and guidance integration</p>
                </div>
                <div className="bg-bg-200 p-3 rounded-lg">
                  <h4 className="font-medium text-text-100 text-sm mb-1">Transcripts Score</h4>
                  <p className="text-xs text-text-200">NLP sentiment analysis of earnings call transcripts and forward-looking statements</p>
                </div>
                <div className="bg-bg-200 p-3 rounded-lg">
                  <h4 className="font-medium text-text-100 text-sm mb-1">Innovation Score</h4>
                  <p className="text-xs text-text-200">Patent quality, talent acquisition, and R&D efficiency analysis</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h3 className="font-medium text-text-100 mb-4 text-lg">Implementation Quick Tips</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-bg-200 p-4 rounded-lg">
                <h4 className="font-medium text-text-100 text-sm mb-2">Model Selection</h4>
                <p className="text-xs text-text-200">Choose based on client risk tolerance, return expectations, and tracking error preferences</p>
              </div>
              <div className="bg-bg-200 p-4 rounded-lg">
                <h4 className="font-medium text-text-100 text-sm mb-2">Rebalancing</h4>
                <p className="text-xs text-text-200">Execute trades in first 3 trading days of month using provided CSV files</p>
              </div>
              <div className="bg-bg-200 p-4 rounded-lg">
                <h4 className="font-medium text-text-100 text-sm mb-2">Compliance</h4>
                <p className="text-xs text-text-200">Complete due diligence documentation before implementation using model factsheets</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full bg-bg-200">
        <DisclaimerFooter small />
      </div>
    </div>
  );
};

export default QuickReferencePage; 